import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Building2,
  DollarSign,
  Users,
  Settings,
  LogOut,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Zap,
  Phone,
  RefreshCw,
  Save,
  TestTube,
  Eye,
  EyeOff,
  Server,
  Key,
  Hash,
  Globe,
  UserCheck,
  X,
  BarChart3,
  Megaphone,
  Sun,
  Moon,
  HelpCircle,
  Send,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [dialers, setDialers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isImpersonation, setIsImpersonation] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState(null);
  
  // Theme state - default to dark, persist in localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('client_portal_theme');
    return saved ? saved === 'dark' : true;
  });
  
  // Dialer config modal
  const [configDialer, setConfigDialer] = useState(null);
  const [dialerConfig, setDialerConfig] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  // Availability toggle
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  
  // Support ticket state
  const [supportTickets, setSupportTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', priority: 'medium' });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);
  
  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('client_portal_theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    
    // Check for impersonation
    const impersonation = localStorage.getItem('is_impersonation');
    const impersonator = localStorage.getItem('impersonated_by');
    if (impersonation === 'true') {
      setIsImpersonation(true);
      setImpersonatedBy(impersonator);
    }
    
    fetchDashboard();
    fetchDialers();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-portal/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
      
      // Also check impersonation from response
      if (response.data.impersonation?.is_impersonation) {
        setIsImpersonation(true);
        setImpersonatedBy(response.data.impersonation.impersonated_by);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_info');
        localStorage.removeItem('is_impersonation');
        localStorage.removeItem('impersonated_by');
        navigate('/client-portal');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDialers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-portal/dialers`, {
        headers: getAuthHeaders()
      });
      setDialers(response.data);
    } catch (error) {
      console.error('Failed to fetch dialers:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_info');
    localStorage.removeItem('is_impersonation');
    localStorage.removeItem('impersonated_by');
    
    if (isImpersonation) {
      // If impersonating, close the tab
      window.close();
    } else {
      navigate('/client-portal');
    }
  };

  const handleEndImpersonation = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_info');
    localStorage.removeItem('is_impersonation');
    localStorage.removeItem('impersonated_by');
    window.close();
  };

  const handleToggleAvailability = async () => {
    if (!dashboard) return;
    
    setTogglingAvailability(true);
    try {
      const newStatus = !dashboard.client.is_available;
      await axios.put(`${API_URL}/api/client-portal/availability`, 
        { is_available: newStatus },
        { headers: getAuthHeaders() }
      );
      setDashboard(prev => ({
        ...prev,
        client: { ...prev.client, is_available: newStatus }
      }));
      toast.success(newStatus ? 'You are now available to receive leads' : 'You are now unavailable');
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setTogglingAvailability(false);
    }
  };

  const openDialerConfig = (dialer) => {
    setConfigDialer(dialer);
    // Initialize config based on dialer type
    const initialConfig = {};
    if (dialer.dialer_type === 'convoso') {
      initialConfig.auth_token = '';
      initialConfig.list_id = dialer.list_id || '0';
      initialConfig.adaptor_id = dialer.adaptor_id || '';
      initialConfig.check_dup = '0';
      initialConfig.check_dnc = '0';
      initialConfig.hopper = '1';
      initialConfig.hopper_priority = '99';
    } else if (dialer.dialer_type === 'vichi') {
      initialConfig.server_url = dialer.server_url || '';
      initialConfig.user = '';
      initialConfig.pass = '';
      initialConfig.list_id = dialer.list_id || '';
      initialConfig.campaign_id = dialer.campaign_id || '';
      initialConfig.phone_code = '1';
    } else if (dialer.dialer_type === 'tld') {
      initialConfig.subdomain = dialer.subdomain || '';
      initialConfig.api_key = '';
      initialConfig.vendor_id = dialer.vendor_id || '';
    }
    initialConfig.is_active = dialer.is_active !== false;
    setDialerConfig(initialConfig);
    setShowSecrets({});
  };

  const handleSaveDialerConfig = async () => {
    if (!configDialer) return;
    
    setSavingConfig(true);
    try {
      await axios.put(
        `${API_URL}/api/client-portal/dialers/${configDialer.dialer_type}`,
        dialerConfig,
        { headers: getAuthHeaders() }
      );
      toast.success('Configuration saved');
      fetchDialers();
      setConfigDialer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestConnection = async () => {
    if (!configDialer) return;
    
    // First save the config
    setSavingConfig(true);
    try {
      await axios.put(
        `${API_URL}/api/client-portal/dialers/${configDialer.dialer_type}`,
        dialerConfig,
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      toast.error('Failed to save configuration');
      setSavingConfig(false);
      return;
    }
    setSavingConfig(false);
    
    // Then test the connection
    setTestingConnection(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/client-portal/dialers/${configDialer.dialer_type}/test`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
      fetchDialers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const getDialerIcon = (type) => {
    return <Zap className="w-5 h-5" />;
  };

  const getDialerName = (type) => {
    switch (type) {
      case 'convoso': return 'Convoso';
      case 'vichi': return 'VICIDIAL';
      case 'tld': return 'TLD Dialer';
      default: return type;
    }
  };

  // Support ticket functions
  const fetchSupportTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(`${API_URL}/api/client-portal/support-tickets`, {
        headers: getAuthHeaders()
      });
      setSupportTickets(res.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch support tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmittingTicket(true);
    try {
      await axios.post(`${API_URL}/api/client-portal/support-tickets`, newTicket, {
        headers: getAuthHeaders()
      });
      toast.success('Support ticket submitted successfully');
      setNewTicket({ subject: '', message: '', priority: 'medium' });
      fetchSupportTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a14]' : 'bg-slate-50'}`}>
        <div className="flex flex-col items-center gap-4">
          <div className={`animate-spin w-10 h-10 border-4 ${isDarkMode ? 'border-purple-500' : 'border-purple-600'} border-t-transparent rounded-full`} />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a14]' : 'bg-slate-50'}`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Failed to load dashboard</p>
          <Button onClick={handleLogout} className="mt-4 bg-purple-600 hover:bg-purple-700">Return to Login</Button>
        </div>
      </div>
    );
  }

  // Theme-based styles
  const theme = {
    bg: isDarkMode ? '#0a0a14' : '#f8fafc',
    headerBg: isDarkMode ? '#12121e' : '#ffffff',
    headerBorder: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
    cardBg: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
    cardBorder: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
    text: isDarkMode ? '#ffffff' : '#1e293b',
    textMuted: isDarkMode ? '#9ca3af' : '#64748b',
    textSubtle: isDarkMode ? '#6b7280' : '#94a3b8',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }} data-testid="client-portal-dashboard">
      {/* Impersonation Banner */}
      {isImpersonation && (
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2 px-4 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              <span className="font-medium">
                Impersonating {dashboard.client.company_name}
              </span>
              <span className="text-purple-200">
                (logged in as {impersonatedBy})
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleEndImpersonation}
              className="text-white hover:bg-purple-800"
            >
              <X className="w-4 h-4 mr-1" />
              End Session
            </Button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header 
        className={`sticky ${isImpersonation ? 'top-10' : 'top-0'} z-10`}
        style={{
          backgroundColor: theme.headerBg,
          borderBottom: `1px solid ${theme.headerBorder}`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/images/vital-reach-logo.jpg" 
              alt="Vital Reach Media" 
              className="h-10 w-auto rounded-lg"
            />
            <div>
              <h1 className="font-semibold" style={{ color: theme.text }}>{dashboard.client.company_name}</h1>
              <p className="text-sm" style={{ color: theme.textMuted }}>Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: theme.textMuted }}>Available for Leads:</span>
              <Switch
                checked={dashboard.client.is_available}
                onCheckedChange={handleToggleAvailability}
                disabled={togglingAvailability}
                className="data-[state=checked]:bg-purple-600"
              />
            </div>
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              className={isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className={isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList 
            className="mb-6 p-1 rounded-xl"
            style={{ 
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.cardBorder}`
            }}
          >
            <TabsTrigger 
              value="overview" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <Building2 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="campaigns" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="client-dashboard-tab-campaigns"
            >
              <Megaphone className="w-4 h-4" />
              Campaigns
            </TabsTrigger>
            <TabsTrigger 
              value="business_leads" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="client-dashboard-tab-business-leads"
            >
              <Building2 className="w-4 h-4" />
              Business Leads
            </TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <BarChart3 className="w-4 h-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger 
              value="dialers" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <Phone className="w-4 h-4" />
              Dialer Integrations
            </TabsTrigger>
            <TabsTrigger 
              value="twilio" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <Zap className="w-4 h-4" />
              Twilio Flex
            </TabsTrigger>
            <TabsTrigger 
              value="ringba" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <BarChart3 className="w-4 h-4" />
              Ringba
            </TabsTrigger>
            <TabsTrigger 
              value="leads" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <Users className="w-4 h-4" />
              Recent Leads
            </TabsTrigger>
            <TabsTrigger 
              value="support" 
              className={`flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
              onClick={() => fetchSupportTickets()}
            >
              <HelpCircle className="w-4 h-4" />
              Support
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card 
                className="border-0"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: theme.textMuted }}>
                    Current Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="text-3xl font-bold font-mono"
                    style={{
                      background: 'linear-gradient(135deg, #a855f7, #c084fc)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    ${dashboard.client.balance?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="border-0"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: theme.textMuted }}>
                    Total Leads Received
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ color: theme.text }}>
                    {dashboard.stats.total_leads || 0}
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="border-0"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: theme.textMuted }}>
                    Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {dashboard.client.is_available ? (
                      <Badge className="bg-purple-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Available
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-600">
                        <XCircle className="w-3 h-3 mr-1" />
                        Unavailable
                      </Badge>
                    )}
                    <Badge style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9', color: theme.textMuted, border: 'none' }}>{dashboard.client.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lead Types */}
            <Card 
              className="border-0"
              style={{
                backgroundColor: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.text }}>Lead Types Enabled</CardTitle>
                <CardDescription style={{ color: theme.textMuted }}>Types of leads you are configured to receive</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {dashboard.settings.receive_cold_calls && (
                    <Badge className="px-4 py-2 bg-purple-600/20 text-purple-400 border border-purple-500/30">Cold Calls</Badge>
                  )}
                  {dashboard.settings.receive_live_transfers && (
                    <Badge className="px-4 py-2 bg-purple-600/20 text-purple-400 border border-purple-500/30">Live Transfers</Badge>
                  )}
                  {dashboard.settings.receive_data_leads && (
                    <Badge className="px-4 py-2 bg-purple-600/20 text-purple-400 border border-purple-500/30">Data Leads</Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            <Card 
              className="border-0"
              style={{
                backgroundColor: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.text }}>Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.recent_transactions?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.recent_transactions.slice(0, 5).map((tx, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${theme.cardBorder}` }}
                      >
                        <div>
                          <p className="font-medium" style={{ color: theme.text }}>{tx.description}</p>
                          <p className="text-xs" style={{ color: theme.textSubtle }}>{new Date(tx.created_at).toLocaleString()}</p>
                        </div>
                        <span className={`font-mono font-medium ${tx.type === 'credit' ? 'text-green-500' : 'text-red-500'}`}>
                          {tx.type === 'credit' ? '+' : '-'}${Math.abs(tx.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8" style={{ color: theme.textSubtle }}>No transactions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dialers Tab */}
          <TabsContent value="dialers" className="space-y-6">
            <Card 
              className="border-0"
              style={{
                backgroundColor: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`
              }}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.text }}>
                  <Phone className="w-5 h-5 text-purple-500" />
                  Dialer Integrations
                </CardTitle>
                <CardDescription style={{ color: theme.textMuted }}>
                  Configure your dialer API connections. Leads will be automatically pushed to your active dialers.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {dialers.map((dialer) => (
                    <Card 
                      key={dialer.dialer_type} 
                      className="border"
                      style={{
                        backgroundColor: dialer.is_configured && dialer.is_active 
                          ? (isDarkMode ? 'rgba(147, 51, 234, 0.1)' : '#faf5ff') 
                          : theme.cardBg,
                        borderColor: dialer.is_configured && dialer.is_active 
                          ? '#9333ea' 
                          : theme.cardBorder
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getDialerIcon(dialer.dialer_type)}
                            <span className="font-semibold" style={{ color: theme.text }}>{getDialerName(dialer.dialer_type)}</span>
                          </div>
                          {!dialer.enabled_by_admin ? (
                            <Badge className="bg-gray-600">Not Enabled</Badge>
                          ) : dialer.is_configured && dialer.is_active ? (
                            <Badge className="bg-purple-600">Active</Badge>
                          ) : dialer.is_configured ? (
                            <Badge variant="outline">Configured</Badge>
                          ) : (
                            <Badge variant="outline">Not Configured</Badge>
                          )}
                        </div>
                        
                        {dialer.enabled_by_admin && (
                          <>
                            {dialer.last_test_status && (
                              <div className="mb-3 text-sm">
                                <span className={dialer.last_test_status === 'success' ? 'text-emerald-600' : 'text-red-600'}>
                                  Last test: {dialer.last_test_status}
                                </span>
                              </div>
                            )}
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => openDialerConfig(dialer)}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Configure
                            </Button>
                          </>
                        )}
                        
                        {!dialer.enabled_by_admin && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Contact your admin to enable this integration.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Twilio Flex Tab */}
          <TabsContent value="twilio" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Zap className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle>Twilio Flex Integration</CardTitle>
                    <CardDescription>Configure your Twilio Flex contact center</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Connect your Twilio Flex account to enable advanced call routing, IVR, and agent management for your leads.
                </p>
                <Button onClick={() => navigate('/client-portal/twilio')} className="bg-teal-600 hover:bg-teal-700">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Twilio Flex
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ringba Tab */}
          <TabsContent value="ringba" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Ringba Call Tracking</CardTitle>
                    <CardDescription>Configure your Ringba call tracking integration</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Connect your Ringba account for advanced call tracking, analytics, real-time bidding, and call routing optimization.
                </p>
                <Button onClick={() => navigate('/client-portal/ringba')} className="bg-orange-600 hover:bg-orange-700">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Ringba
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Leads</CardTitle>
                <CardDescription>Your most recently received leads</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard.recent_leads?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.recent_leads.map((lead, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">Lead #{lead.lead_id?.slice(-8) || i+1}</p>
                          <p className="text-sm text-muted-foreground">{lead.lead_type}</p>
                          <p className="text-xs text-muted-foreground">{new Date(lead.assigned_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-red-600">-${lead.cost?.toFixed(2)}</p>
                          <Badge variant="outline">{lead.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No leads received yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Campaign Management
                </CardTitle>
                <CardDescription>Create and manage your lead campaigns</CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium mb-2">Full Campaigns Dashboard</h3>
                <p className="text-muted-foreground mb-4">
                  Create campaigns, set targeting by state, configure budgets, and more
                </p>
                <Button onClick={() => navigate('/client-portal/campaigns')} className="bg-teal-600 hover:bg-teal-700">
                  Open Campaigns Dashboard
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="business_leads" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Business Leads Feed
                </CardTitle>
                <CardDescription>See source type, filters, lead counts, drip queue, and costs.</CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium mb-2">Business Leads Dashboard</h3>
                <p className="text-muted-foreground mb-4">
                  Review your business lead campaigns, drip feed progress, and billing per lead.
                </p>
                <Button data-testid="client-dashboard-open-business-leads" onClick={() => navigate('/client-portal/business-leads')} className="bg-purple-600 hover:bg-purple-700">
                  Open Business Leads
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Reports
                </CardTitle>
                <CardDescription>Generate and export lead reports</CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium mb-2">Full Reports Page</h3>
                <p className="text-muted-foreground mb-4">
                  Access detailed reports with filters, charts, and export options
                </p>
                <Button onClick={() => window.open('/client-portal/reports', '_blank')}>
                  Open Reports Dashboard
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Submit New Ticket */}
              <Card 
                className="border-0"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`
                }}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ color: theme.text }}>
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    Submit a Support Ticket
                  </CardTitle>
                  <CardDescription style={{ color: theme.textMuted }}>
                    Need help? Submit a ticket and our team will respond shortly.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitTicket} className="space-y-4">
                    <div className="space-y-2">
                      <Label style={{ color: theme.text }}>Subject</Label>
                      <Input
                        value={newTicket.subject}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Brief description of your issue"
                        className={isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: theme.text }}>Priority</Label>
                      <Select 
                        value={newTicket.priority} 
                        onValueChange={(v) => setNewTicket(prev => ({ ...prev, priority: v }))}
                      >
                        <SelectTrigger className={isDarkMode ? 'bg-white/5 border-white/10 text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - General question</SelectItem>
                          <SelectItem value="medium">Medium - Need assistance</SelectItem>
                          <SelectItem value="high">High - Urgent issue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: theme.text }}>Message</Label>
                      <textarea
                        value={newTicket.message}
                        onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Please describe your issue in detail..."
                        rows={5}
                        className={`w-full px-3 py-2 rounded-md border text-sm ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' 
                            : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                        } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      disabled={submittingTicket}
                    >
                      {submittingTicket ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Ticket
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Recent Tickets */}
              <Card 
                className="border-0"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`
                }}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ color: theme.text }}>
                    <HelpCircle className="w-5 h-5 text-purple-500" />
                    Your Tickets
                  </CardTitle>
                  <CardDescription style={{ color: theme.textMuted }}>
                    View status of your support requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingTickets ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                    </div>
                  ) : supportTickets.length > 0 ? (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {supportTickets.map((ticket) => (
                        <div 
                          key={ticket.id} 
                          className="p-3 rounded-lg"
                          style={{ 
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            border: `1px solid ${theme.cardBorder}`
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium" style={{ color: theme.text }}>{ticket.subject}</h4>
                            <Badge className={
                              ticket.status === 'open' ? 'bg-yellow-500' :
                              ticket.status === 'in_progress' ? 'bg-blue-500' :
                              ticket.status === 'resolved' ? 'bg-green-500' :
                              'bg-gray-500'
                            }>
                              {ticket.status?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm mb-2 line-clamp-2" style={{ color: theme.textMuted }}>
                            {ticket.message}
                          </p>
                          <div className="flex items-center justify-between text-xs" style={{ color: theme.textSubtle }}>
                            <span>Priority: <span className={
                              ticket.priority === 'high' ? 'text-red-500' :
                              ticket.priority === 'medium' ? 'text-yellow-500' :
                              'text-green-500'
                            }>{ticket.priority}</span></span>
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8" style={{ color: theme.textMuted }}>
                      <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No support tickets yet</p>
                      <p className="text-sm mt-1">Submit a ticket if you need help</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialer Configuration Modal */}
      <Dialog open={!!configDialer} onOpenChange={() => setConfigDialer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {configDialer && getDialerIcon(configDialer.dialer_type)}
              Configure {configDialer && getDialerName(configDialer.dialer_type)}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to enable lead delivery
            </DialogDescription>
          </DialogHeader>

          {configDialer?.dialer_type === 'convoso' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Auth Token *
                </Label>
                <div className="relative">
                  <Input
                    type={showSecrets.auth_token ? 'text' : 'password'}
                    value={dialerConfig.auth_token || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, auth_token: e.target.value})}
                    placeholder="Your Convoso auth token"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowSecrets({...showSecrets, auth_token: !showSecrets.auth_token})}
                  >
                    {showSecrets.auth_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Find this in your Convoso dashboard under API settings</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    List ID
                  </Label>
                  <Input
                    value={dialerConfig.list_id || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, list_id: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adaptor ID</Label>
                  <Input
                    value={dialerConfig.adaptor_id || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, adaptor_id: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-muted-foreground">Receive leads to this dialer</p>
                </div>
                <Switch
                  checked={dialerConfig.is_active !== false}
                  onCheckedChange={(checked) => setDialerConfig({...dialerConfig, is_active: checked})}
                />
              </div>
            </div>
          )}

          {configDialer?.dialer_type === 'vichi' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Server URL *
                </Label>
                <Input
                  value={dialerConfig.server_url || ''}
                  onChange={(e) => setDialerConfig({...dialerConfig, server_url: e.target.value})}
                  placeholder="https://your-server.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Username *</Label>
                  <Input
                    value={dialerConfig.user || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, user: e.target.value})}
                    placeholder="API user"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Password *</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.pass ? 'text' : 'password'}
                      value={dialerConfig.pass || ''}
                      onChange={(e) => setDialerConfig({...dialerConfig, pass: e.target.value})}
                      placeholder="API password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecrets({...showSecrets, pass: !showSecrets.pass})}
                    >
                      {showSecrets.pass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>List ID</Label>
                  <Input
                    value={dialerConfig.list_id || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, list_id: e.target.value})}
                    placeholder="List ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Campaign ID</Label>
                  <Input
                    value={dialerConfig.campaign_id || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, campaign_id: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-muted-foreground">Receive leads to this dialer</p>
                </div>
                <Switch
                  checked={dialerConfig.is_active !== false}
                  onCheckedChange={(checked) => setDialerConfig({...dialerConfig, is_active: checked})}
                />
              </div>
            </div>
          )}

          {configDialer?.dialer_type === 'tld' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Subdomain *
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={dialerConfig.subdomain || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, subdomain: e.target.value})}
                    placeholder="yourcompany"
                  />
                  <span className="text-muted-foreground whitespace-nowrap">.tldcrm.com</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  API Key *
                </Label>
                <div className="relative">
                  <Input
                    type={showSecrets.api_key ? 'text' : 'password'}
                    value={dialerConfig.api_key || ''}
                    onChange={(e) => setDialerConfig({...dialerConfig, api_key: e.target.value})}
                    placeholder="Your TLD API key"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowSecrets({...showSecrets, api_key: !showSecrets.api_key})}
                  >
                    {showSecrets.api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Vendor ID</Label>
                <Input
                  value={dialerConfig.vendor_id || ''}
                  onChange={(e) => setDialerConfig({...dialerConfig, vendor_id: e.target.value})}
                  placeholder="Optional"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-muted-foreground">Receive leads to this dialer</p>
                </div>
                <Switch
                  checked={dialerConfig.is_active !== false}
                  onCheckedChange={(checked) => setDialerConfig({...dialerConfig, is_active: checked})}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={savingConfig || testingConnection}
            >
              {testingConnection ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4 mr-2" />
              )}
              Test Connection
            </Button>
            <Button
              onClick={handleSaveDialerConfig}
              disabled={savingConfig}
            >
              {savingConfig ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
